import { useState } from 'react'
import { canWriteArea, localDayKey, type AttendanceReportStamp, type PublicUserDTO } from '@wf/shared'
import { useAttendanceToday } from '../attendance/useAttendance'
import { downloadTextFile } from '../export/csv'
import { formatHMS } from '../time/format'
import { AddStampForm } from './AddStampForm'
import { ArbZgFlagsCard } from './ArbZgFlagsCard'
import { ArbzgAverageCard } from './ArbzgAverageCard'
import { ReportTable } from './ReportTable'
import { TimeAccountCard } from './TimeAccountCard'
import { VoidStampPanel } from './VoidStampPanel'
import { reportCsv } from './reportCsv'
import { isoWeekKey, monthLabel, monthRange, paddedMonthRange, pauseSecondsByDay, shiftMonth, weekSecondsFromDays } from './timeMath'
import { absenceRangeLabel } from './AbsenceRequestCard'
import { useArbzgAck, useTimesheet, useAttendanceReport } from './useZeiten'

type Panel = { kind: 'none' } | { kind: 'void'; stamp: AttendanceReportStamp } | { kind: 'add'; day: string }

/**
 * Chef-Nachweis (W9.2, isBoss): Monatsnachweis JE Mitarbeiter mit Kennzahlen (§16-ArbZG->8h-Tage,
 * >48h-Wochen-Warnung), Stempel entwerten/nachtragen (revisionssicher) und Zoll-tauglichem CSV-Export
 * (§17 MiLoG: deutsch, Beginn/Ende/Dauer). Geladen wird der Monat ±6 Tage, damit die >48h-Warnung volle
 * ISO-Wochen sieht (Randwochen wären sonst systematisch unterzählt); Tabelle/Kennzahlen/CSV filtern
 * wieder auf den echten Monat. MA-Auswahl aus der Team-Tagesliste (bis /users kommt, W11).
 */
export function NachweisPanel({ user, initialUserId }: { user: PublicUserDTO; initialUserId?: number }) {
  const [month, setMonth] = useState(() => localDayKey(Date.now()).slice(0, 7))
  // initialUserId: Sprung aus Heute/Auswertung (✏️) — der Aufrufer remountet per key bei Wechsel.
  const [userId, setUserId] = useState<number>(initialUserId ?? user.id)
  const [panel, setPanel] = useState<Panel>({ kind: 'none' })
  const team = useAttendanceToday()
  const { from, to } = monthRange(month)
  const padded = paddedMonthRange(month)
  const report = useAttendanceReport(padded.from, padded.to, userId)
  // GEPUFFERT laden, wie der Report: der Perioden-Wächter rechnet auf genau den übergebenen Tagen, ein
  // monatsgenaues Fenster schneidet also jede Rand-Woche an — sie meldete dann systematisch zu wenig
  // (und trüge obendrein dauerhaft den „unvollständig"-Hinweis). Auf den echten Monat gefiltert wird
  // erst bei der Anzeige, per Überschneidung.
  const timesheet = useTimesheet(padded.from, padded.to, userId)
  const ack = useArbzgAck()
  const canAck = canWriteArea(user, 'arbeitszeit') // Quittieren = fremde Arbeitszeit verwalten (arbeitszeit:bearbeiten, Rang 90)
  const close = () => setPanel({ kind: 'none' })

  const staff = team.data?.entries ?? []
  const selectedName = staff.find((e) => e.userId === userId)?.name ?? report.data?.user.name ?? user.name

  // Monats-Sicht = Report auf den echten Monat gefiltert (der ±6-Tage-Puffer dient NUR den Wochen-Summen).
  const monthDays = (report.data?.days ?? []).filter((d) => d.day >= from && d.day <= to)
  const monthStamps = (report.data?.stamps ?? []).filter((s) => {
    const day = localDayKey(s.tsMs)
    return day >= from && day <= to
  })
  const monthReport = report.data ? { ...report.data, from, to, days: monthDays, stamps: monthStamps } : undefined

  const totalSec = monthDays.reduce((sum, d) => sum + d.workedSeconds, 0)
  const overDays = monthDays.filter((d) => d.workedSeconds > 8 * 3600).length
  const corrCount = monthStamps.filter((s) => s.voided || s.correctedBy != null).length
  // Wochen-Summen über den GEPUFFERTEN Zeitraum; gewarnt wird nur für Wochen, die den Monat berühren.
  //
  // ⚠️ BEWUSST die reinen STEMPEL (report.days), nicht das Zeitkonto-Ist des Timesheets. Ein zwischenzeitlicher
  // Umbau auf `weeks[].istSeconds` sollte die Berufsschul-Gutschrift einrechnen, holte damit aber Urlaub und
  // Krankheit mit herein: „Mo+Di Urlaub + Mi–Sa je 8,5 h" (real 34 h gearbeitet) schlug als 50-h-Woche an.
  // § 3 ArbZG begrenzt die ARBEITSZEIT — bezahlte Abwesenheit ist keine. Dieselbe Unterscheidung trifft
  // packages/shared/src/time.ts (jarbschgArbeitszeit statt istSeconds), und sie gilt hier genauso.
  // Für Azubis greift zusätzlich der Perioden-Wächter mit der 40-h-Grenze; der rechnet die Berufsschulzeit
  // korrekt ein und kommt als periodFlag in dieselbe Karte.
  const monthWeekKeys = new Set(monthDays.map((d) => isoWeekKey(d.day)))
  const over48Weeks = Object.entries(weekSecondsFromDays(report.data?.days ?? []))
    .filter(([week, sec]) => sec > 48 * 3600 && monthWeekKeys.has(week))
    .map(([week]) => week)
  // R4/1.8: schicht-basierte ArbZG-Verstöße (§3/§4/§5) des Servers, auf den echten Monat gefiltert
  // (der ±6-Tage-Puffer dient nur den Ruhezeit-/Wochen-Kontexten).
  const monthFlags = (report.data?.flags ?? []).filter((f) => {
    const day = localDayKey(f.shiftStartMs)
    return day >= from && day <= to
  })
  // JArbSchG-Perioden-Flags (Wochen/Monate) aus dem Timesheet. Gefiltert wird per ÜBERSCHNEIDUNG, nicht per
  // Anker-Tag: ein Wochen-Flag ankert auf dem Montag, der bei einem Monat, der mitten in der Woche beginnt,
  // im Vormonat liegt — es fiele sonst aus genau dem Dokument, das es belegen soll.
  const monthPeriodFlags = (timesheet.data?.periodFlags ?? []).filter(
    (f) => localDayKey(f.shiftEndMs) >= from && localDayKey(f.shiftStartMs) <= to,
  )

  function exportCsv() {
    if (!monthReport) return
    const safeName = monthReport.user.name.replace(/[^\wäöüÄÖÜß-]+/g, '_')
    downloadTextFile(`zeitnachweis-${safeName}-${month}.csv`, reportCsv(monthReport, pauseSecondsByDay(monthReport.stamps)))
  }

  return (
    <div className="wf-view wf-zstack">
      <div className="wf-ztnav">
        <select
          className="wf-input"
          aria-label="Mitarbeiter wählen"
          value={userId}
          onChange={(e) => {
            setUserId(Number(e.target.value))
            close()
          }}
        >
          {staff.length === 0 ? <option value={user.id}>{user.name}</option> : null}
          {staff.map((entry) => (
            <option key={entry.userId} value={entry.userId}>
              {entry.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="wf-btn wf-btn--ghost wf-btn--sm"
          aria-label="Voriger Monat"
          onClick={() => setMonth((m) => shiftMonth(m, -1))}
        >
          ◀
        </button>
        <span className="wf-ztnav__month">{monthLabel(month)}</span>
        <button
          type="button"
          className="wf-btn wf-btn--ghost wf-btn--sm"
          aria-label="Nächster Monat"
          onClick={() => setMonth((m) => shiftMonth(m, 1))}
        >
          ▶
        </button>
      </div>
      {team.isLoading ? <p className="hint">Team-Liste wird geladen…</p> : null}
      {team.isError ? (
        <p className="hint" role="status">
          Team-Liste nicht verfügbar — aktuell ist nur dein eigener Nachweis wählbar.
        </p>
      ) : null}

      <div className="grid cards4 wf-zskpis">
        <div className="wf-stat wf-stat--fertig">
          <div className="wf-stat__label">Netto gesamt</div>
          <div className="wf-stat__value">{formatHMS(totalSec)}</div>
          <div className="wf-stat__sub">im Monat</div>
        </div>
        <div className="wf-stat wf-stat--offen">
          <div className="wf-stat__label">Arbeitstage</div>
          <div className="wf-stat__value">{monthDays.length}</div>
          <div className="wf-stat__sub">mit Stempeln</div>
        </div>
        <div className={overDays ? 'wf-stat wf-stat--arbeit' : 'wf-stat'}>
          <div className="wf-stat__label">Tage über 8 Std.</div>
          <div className="wf-stat__value">{overDays}</div>
          <div className="wf-stat__sub">§16 ArbZG</div>
        </div>
        <div className={corrCount ? 'wf-stat wf-stat--arbeit' : 'wf-stat'}>
          <div className="wf-stat__label">Korrekturen</div>
          <div className="wf-stat__value">{corrCount}</div>
          <div className="wf-stat__sub">dokumentiert</div>
        </div>
      </div>

      {over48Weeks.length > 0 ? (
        <div className="wf-ztwarn" role="alert">
          <span aria-hidden="true">⚠</span>
          <span>Wochenarbeitszeit über 48 Std. in: {over48Weeks.join(', ')} (Schutzgrenze ArbZG)</span>
        </div>
      ) : null}

      <ArbZgFlagsCard
        flags={monthFlags}
        canAck={canAck}
        acking={ack.isPending}
        onAck={(f) => ack.mutate({ userId, shiftStartMs: f.shiftStartMs, type: f.type })}
      />
      {/* Perioden-Flags bewusst OHNE Quittier-Knopf: das Timesheet liefert (anders als der Report) kein
          `acked` zurück, ein Klick bliebe also dauerhaft wirkungslos und würde eine Bearbeitung vortäuschen,
          die nie ankommt. Der Rückkanal gehört in einen eigenen Schritt, nicht in eine tote Schaltfläche. */}
      {timesheet.isError ? (
        <div className="wf-ztwarn" role="alert">
          <span aria-hidden="true">⚠</span>
          <span>Die Jugendschutz-Prüfung (Wochen- und Monatsregeln) konnte nicht geladen werden — bitte neu laden.</span>
        </div>
      ) : null}
      <ArbZgFlagsCard flags={monthPeriodFlags} label="Jugendschutz: Wochen- und Monatsregeln" />

      {/* § 3 ArbZG: der Acht-Stunden-DURCHSCHNITT über das rollierende Ausgleichsfenster. Bewusst NICHT an
          den Monat der Nachweis-Navigation gekoppelt — das Fenster endet immer heute, jede andere Wahl
          wäre die Einladung, sich den günstigsten Ausschnitt zu suchen. */}
      <ArbzgAverageCard userId={userId} />

      {(report.data?.absences ?? []).filter((a) => a.toDay >= from && a.fromDay <= to).length > 0 ? (
        <div className="wf-card" role="region" aria-label="Abwesenheiten im Monat">
          <h3 className="wf-card__title">Abwesenheiten</h3>
          <ul className="wf-abslist">
            {(report.data?.absences ?? [])
              .filter((a) => a.toDay >= from && a.fromDay <= to)
              .map((a) => (
                <li key={a.id} className="wf-abslist__row">
                  <span>
                    <b>{a.label}</b> {absenceRangeLabel(a)}
                  </span>
                  <span className="wf-tag wf-abs--genehmigt">genehmigt</span>
                </li>
              ))}
          </ul>
        </div>
      ) : null}

      <TimeAccountCard userId={userId} canManage={canAck} />

      <div className="wf-sectionhead">
        <h3>Monatsnachweis · {selectedName}</h3>
        <div className="wf-adminrow__actions">
          <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={exportCsv} disabled={!monthReport}>
            CSV-Export
          </button>
          <button
            type="button"
            className="wf-btn wf-btn--ghost wf-btn--sm"
            onClick={() => setPanel({ kind: 'add', day: localDayKey(Date.now()) })}
          >
            + Stempel nachtragen
          </button>
        </div>
      </div>

      {panel.kind === 'void' ? <VoidStampPanel key={`void-${panel.stamp.id}`} stamp={panel.stamp} onDone={close} onClose={close} /> : null}
      {panel.kind === 'add' ? (
        // key: bei Tag-/MA-Wechsel frisch mounten — sonst bleibt der alte Prefill im useState hängen (Review-Fund).
        <AddStampForm key={`add-${userId}-${panel.day}`} userId={userId} userName={selectedName} initialDay={panel.day} onSuccess={close} onClose={close} />
      ) : null}

      {report.isLoading ? <div className="muted">Nachweis wird geladen…</div> : null}
      {report.isError ? (
        <div className="error" role="alert">
          Nachweis konnte nicht geladen werden.
        </div>
      ) : null}
      {monthReport ? (
        <ReportTable
          report={monthReport}
          canEdit
          onVoid={(stamp) => setPanel({ kind: 'void', stamp })}
          onAdd={(day) => setPanel({ kind: 'add', day })}
        />
      ) : null}

      <p className="hint">
        Original-Stempel sind unveränderbar. Nachträge (Akzent-Rand) und Entwertungen (durchgestrichen) bleiben mit Grund, Urheber
        und Zeitpunkt dauerhaft nachvollziehbar. Aufbewahrung: Zeitnachweise mind. <b>2 Jahre</b> (§16 ArbZG/§17 MiLoG),
        lohnrelevant <b>6–8 Jahre</b> (§41 EStG/§147 AO). Kein Rechtsrat — Details in docs/zeitmanagement-recht.md.
      </p>
    </div>
  )
}
